/* Collection & search filtering — progressive enhancement over the GET form.
   Re-renders the results via the Section Rendering API and keeps the URL in
   step (pushState/popstate). After each update it puts focus back on the
   control that was used and announces the new count. Below 990px the filter
   panel moves into a <dialog> drawer (snippets/facets-drawer.liquid). */
(() => {
  'use strict';

  if (window.psjFacetsLoaded) return;
  window.psjFacetsLoaded = true;

  const mobile = window.matchMedia('(max-width: 989px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let debounceTimer;
  let controller;
  let renderedSearch = window.location.search;

  const getRoot = () => document.querySelector('[data-facets-section]');
  const getDrawer = () => document.getElementById('FacetsDrawer');
  const getToggle = () => document.querySelector('[data-facets-toggle]');

  const serializeForm = (form) => {
    const params = new URLSearchParams();
    // FormData includes controls joined with form="…", such as Sort by.
    const defaultSort = document.querySelector('[data-sort-select]')?.dataset.defaultSort;
    for (const [key, value] of new FormData(form).entries()) {
      if (value === '' || (key === 'sort_by' && value === defaultSort)) continue;
      params.append(key, value);
    }
    return params.toString();
  };

  /* ---------- Filter drawer (phones and tablets) ---------- */

  // One copy of the filters: in the sidebar on desktop, in the drawer below.
  const placePanel = () => {
    const panel = document.getElementById('FacetsPanel');
    const sidebar = document.getElementById('FacetsSidebar');
    const drawer = getDrawer();
    if (!panel || !sidebar || !drawer) return;
    const body = drawer.querySelector('[data-facets-drawer-body]');
    if (mobile.matches) {
      if (panel.parentElement !== body) body.append(panel);
    } else {
      if (drawer.open) drawer.close();
      if (panel.parentElement !== sidebar) sidebar.append(panel);
    }
  };

  const bindDrawer = () => {
    const drawer = getDrawer();
    if (!drawer || drawer.dataset.facetsBound) return;
    drawer.dataset.facetsBound = 'true';
    // The toggle may have been re-rendered while the drawer was open, so focus
    // the current one rather than relying on the dialog's own focus return.
    drawer.addEventListener('close', () => {
      const toggle = getToggle();
      if (!toggle) return;
      toggle.setAttribute('aria-expanded', 'false');
      if (mobile.matches) toggle.focus();
    });
  };

  const openDrawer = () => {
    const drawer = getDrawer();
    if (!drawer) return;
    placePanel();
    if (!drawer.open) drawer.showModal();
    getToggle()?.setAttribute('aria-expanded', 'true');
  };

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-facets-toggle]')) {
      event.preventDefault();
      openDrawer();
      return;
    }

    const closer = event.target.closest('[data-facets-drawer-close]');
    if (closer) {
      closer.closest('dialog')?.close();
      return;
    }

    // The drawer's children fill it, so a click on the dialog itself is a
    // click on the backdrop.
    const drawer = getDrawer();
    if (drawer?.open && event.target === drawer) drawer.close();
  });

  /* ---------- Focus and announcements ---------- */

  const describeFocus = () => {
    const el = document.activeElement;
    const root = getRoot();
    if (!el || !root || !root.contains(el)) return null;
    return {
      id: el.id || null,
      name: el.getAttribute('name'),
      value: el.getAttribute('value'),
      inDrawer: Boolean(el.closest('[data-facets-drawer]')),
    };
  };

  const restoreFocus = (before) => {
    if (!before) return;
    const root = getRoot();
    let target = before.id ? document.getElementById(before.id) : null;
    if (!target && before.name && root) {
      target = [...root.querySelectorAll('[name]')].find(
        (el) =>
          el.getAttribute('name') === before.name &&
          (before.value === null || el.getAttribute('value') === before.value)
      );
    }
    // A removed pill, a vanished page link or a now-disabled value: fall back
    // to the drawer's title, or to the result count above the grid.
    if (!target || target.disabled || target.closest('[hidden]')) {
      target = document.getElementById(before.inDrawer ? 'FacetsDrawerTitle' : 'ResultsCount');
    }
    target?.focus({ preventScroll: true });
  };

  const announce = (text) => {
    const status = document.querySelector('[data-facets-status]');
    if (!status || !text) return;
    status.textContent = '';
    setTimeout(() => {
      status.textContent = text;
    }, 150);
  };

  /* ---------- Rendering ---------- */

  const renderFromUrl = async (search, { updateHistory = true, scrollToTop = false } = {}) => {
    const root = getRoot();
    if (!root) return;
    const before = describeFocus();
    const results = root.querySelector('[data-facets-results]');

    controller?.abort();
    const request = new AbortController();
    controller = request;
    root.classList.add('is-loading');
    results?.setAttribute('aria-busy', 'true');

    try {
      const params = new URLSearchParams(search);
      params.set('section_id', root.dataset.sectionId);
      const response = await fetch(`${window.location.pathname}?${params}`, { signal: request.signal });
      if (!response.ok) throw new Error(`Section render failed: ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');

      // Keep any filter group the shopper collapsed collapsed.
      const collapsed = [...root.querySelectorAll('#Facets details:not([open])')].map(
        (details) => details.dataset.filterParam
      );

      ['[data-facets-results]', '#Facets', '[data-facets-drawer-footer]'].forEach((selector) => {
        const next = html.querySelector(selector);
        const current = root.querySelector(selector);
        if (next && current) current.innerHTML = next.innerHTML;
      });

      collapsed.forEach((param) => {
        if (!param) return;
        root
          .querySelector(`#Facets details[data-filter-param="${CSS.escape(param)}"]`)
          ?.removeAttribute('open');
      });

      getToggle()?.setAttribute('aria-expanded', getDrawer()?.open ? 'true' : 'false');

      renderedSearch = search ? `?${search}` : '';
      if (updateHistory) {
        history.pushState({ facets: true }, '', `${window.location.pathname}${renderedSearch}`);
      }

      restoreFocus(before);
      announce(html.querySelector('[data-facets-count]')?.textContent.trim());

      if (scrollToTop) {
        root.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        // Fall back to a full page load
        window.location.search = search;
      }
    } finally {
      if (controller === request) {
        root.classList.remove('is-loading');
        results?.removeAttribute('aria-busy');
      }
    }
  };

  document.addEventListener('change', (event) => {
    const form = event.target.form;
    if (!form || !form.matches('[data-facets-form]')) return;
    const isPrice = event.target.type === 'number';
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => renderFromUrl(serializeForm(form)), isPrice ? 500 : 0);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-facets-form]')) return;
    event.preventDefault();
    clearTimeout(debounceTimer);
    renderFromUrl(serializeForm(form));
  });

  // Facet pills, Clear all and pagination: soft navigation. Modified clicks
  // (new tab, new window) keep their normal behaviour.
  document.addEventListener('click', (event) => {
    const link = event.target.closest('#Facets a, [data-facets-clear], [data-facets-section] .pagination a');
    if (!link || event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const url = new URL(link.href, window.location.origin);
    if (url.pathname !== window.location.pathname) return;
    event.preventDefault();
    url.searchParams.delete('section_id');
    renderFromUrl(url.searchParams.toString(), { scrollToTop: Boolean(link.closest('.pagination')) });
  });

  window.addEventListener('popstate', () => {
    // A fragment change (e.g. the skip link) also fires popstate; only a
    // different query string needs new results.
    if (window.location.search === renderedSearch) return;
    renderFromUrl(window.location.search.replace(/^\?/, ''), { updateHistory: false });
  });

  const init = () => {
    placePanel();
    bindDrawer();
  };

  mobile.addEventListener('change', placePanel);
  document.addEventListener('shopify:section:load', init);
  init();
})();
